import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { ShoppingCart, Menu, X } from 'lucide-react'
import Lenis from 'lenis'
import SiteLink from '@/components/SiteLink'
import { appLink, homeLink, storeLink } from '@/lib/siteLinks'

// Shared public-site header for the marketing homepage (/) and the merch
// store (/merch/*), so moving between them feels like one site. Mirrors the
// Shopify original: a floating liquid-glass pill detached from the top edge,
// with the current page shown as a raised chip and the cart count in
// parentheses. Two visual themes: "light" on the marketing page, "night" on
// the store's dark look. The store passes `cart` ({ count, onOpen }) to turn
// the cart into its drawer trigger; without it the cart just links into the
// store.
//
// The custom designer (/merch/custom) is deliberately unlinked from the whole
// site for now — it's reachable only by people who know the URL.
// The store lives only at getdub.com/merch; Home/Merch and App resolve to
// getdub.com / app.getdub.com when on the other production host, and to
// internal routes everywhere else (previews, dev, native).
const NAV_LINKS = [
  { label: 'Home', to: homeLink() },
  { label: 'Merch', to: storeLink() },
  { label: 'App', to: appLink('/login') },
]

const THEMES = {
  light: {
    pill: (scrolled) =>
      `border-white/60 bg-white/50 shadow-[0_8px_30px_rgba(41,34,55,0.10)] ${
        scrolled ? 'bg-white/60 shadow-[0_10px_34px_rgba(41,34,55,0.16)]' : ''
      }`,
    logo: '/dub-logo.svg',
    link: 'text-[#292237]/70 hover:text-[#292237]',
    linkActive: 'bg-white text-[#292237] shadow-[0_2px_10px_rgba(41,34,55,0.14)]',
    icon: 'text-[#292237] hover:bg-[#292237]/5',
    menu: 'border-white/60 bg-white/85',
    menuLink: 'text-[#292237] hover:bg-[#292237]/5',
  },
  night: {
    pill: () => 'border-[var(--st-border)] bg-[#17131f]/40 shadow-[0_8px_30px_rgba(0,0,0,0.45)]',
    logo: '/dub-logo-white.svg',
    link: 'text-[var(--st-muted)] hover:text-[var(--st-fg)]',
    linkActive: 'bg-[var(--st-surface-2)] text-[var(--st-fg)] shadow-[0_2px_10px_rgba(0,0,0,0.35)]',
    icon: 'text-[var(--st-fg)] hover:bg-[var(--st-surface-2)]',
    menu: 'border-[var(--st-border)] bg-[#17131f]/90',
    menuLink: 'text-[var(--st-muted)] hover:bg-[var(--st-surface-2)] hover:text-[var(--st-fg)]',
  },
}

export default function SiteNav({ theme = 'light', cart = null }) {
  const t = THEMES[theme]
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)
  const { pathname } = useLocation()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // The Shopify original's fluid feel comes from wheel-scroll smoothing
  // (inertia/lerp), not CSS — plain scroll-behavior doesn't touch wheel
  // input. Lenis runs while this nav is mounted, so it's scoped to the
  // public pages; the logged-in app keeps native scrolling.
  useEffect(() => {
    const lenis = new Lenis({ autoRaf: true })
    return () => lenis.destroy()
  }, [])

  const isActive = (to) =>
    to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(to + '/')

  // Clicking the link for the page you're already on should read as "back to
  // the top" rather than doing nothing.
  const onNavClick = (to) => {
    if (to === pathname) window.scrollTo({ top: 0, behavior: 'smooth' })
    setOpen(false)
  }

  const navLink = (l, extra) => (
    <SiteLink
      key={l.label}
      to={l.to}
      onClick={() => onNavClick(l.to)}
      className={`rounded-full px-4 py-1.5 text-[15px] font-medium transition-all ${
        isActive(l.to) ? t.linkActive : t.link
      } ${extra || ''}`}
    >
      {l.label}
    </SiteLink>
  )

  const cartInner = (
    <>
      <ShoppingCart className="h-5 w-5" />
      {cart?.count > 0 && <span className="text-[15px] font-medium">( {cart.count} )</span>}
    </>
  )
  const cartClasses = `flex items-center gap-1.5 rounded-full px-2.5 py-2 transition-colors ${t.icon}`

  return (
    <header className="sticky top-3 z-40 px-3 sm:top-4 sm:px-5">
      <div
        className={`mx-auto flex h-14 max-w-6xl items-center justify-between rounded-full border px-3 pl-5 backdrop-blur-xl backdrop-saturate-150 transition-[background-color,box-shadow] duration-300 sm:px-4 sm:pl-7 ${t.pill(scrolled)}`}
      >
        <SiteLink to={homeLink()} onClick={() => onNavClick(homeLink())} className="flex items-center" aria-label="dub home">
          <img src={t.logo} alt="dub" className="h-7 w-auto" />
        </SiteLink>

        <nav className="hidden items-center gap-2 md:flex">
          {NAV_LINKS.map((l) => navLink(l))}
        </nav>

        <div className="flex items-center gap-1">
          {cart ? (
            <button
              type="button"
              onClick={cart.onOpen}
              className={cartClasses}
              aria-label={`Open cart, ${cart.count} item${cart.count === 1 ? '' : 's'}`}
            >
              {cartInner}
            </button>
          ) : (
            <SiteLink to={storeLink()} className={cartClasses} aria-label="Cart">
              {cartInner}
            </SiteLink>
          )}
          <button
            className={`rounded-full p-2 transition-colors md:hidden ${t.icon}`}
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className={`mx-auto mt-2 max-w-6xl rounded-3xl border p-2 backdrop-blur-xl backdrop-saturate-150 md:hidden ${t.menu}`}
        >
          <nav className="flex flex-col gap-1">
            {NAV_LINKS.map((l) =>
              navLink(l, 'px-4 py-2.5'),
            )}
          </nav>
        </div>
      )}
    </header>
  )
}
